import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { skipToken, useQuery } from '@connectrpc/connect-query'
import { listTradableAreas } from '@ebalance/proto/bffuser/v1/tradable_area_service-TradableAreaService_connectquery'
import {
  ListTradableAreasRequestSchema,
  type ListTradableAreasResponse,
} from '@ebalance/proto/bffuser/v1/tradable_area_service_pb'
import { type UseQueryResult, keepPreviousData } from '@tanstack/react-query'

import { toProtoDate } from '~/models/dateTime'

/**
 * 認証組織が受渡日に取引可能なエリアと、 発注主体区分ごとの登録済みエリア・選べるエリア・
 * 受渡契約コードを取得する。
 *
 * `deliveryDate` は表示中の受渡日 (= カレンダー日。 ローカル 0:00 の `Date`)。 backend は
 * この日に有効な広域マスタで求める (保存時の受渡契約コード解決と同じ断面。 #8842)。
 * 受渡日は query key に入るので、 受渡日を変えると取り直す。
 * `enabled: false` の間は request を出さず、 当日 key の cache も返さない (`skipToken`)。
 *
 * エラー時は data が undefined のまま返る。
 * ページ側では data === undefined を「取得前 / 失敗」として扱い、 全エリア有効
 * (取引可能エリアによる絞り込みなし) とする。ただし失敗時はページ側の
 * `failedDataLabels` に合流し、取得失敗バナーには表示される
 * (`SpotBidding/index.tsx` の `isTradableAreasError`)。 発注主体区分の選択肢
 * ([[useIssuerOptions]]) は取得前・失敗とも 0 件に縮退する。
 *
 * fail-safe: 取引可能エリアの事前チェックはあくまで補助機能なので、 backend gRPC
 * 未接続 (= Unavailable 等) でも error boundary (ErrorLayout = 全画面エラー) に飛ばさない。
 * global QueryClient の `throwOnError: true` を本 query だけ `false` に上書きし、 取得失敗時も
 * error boundary (全画面エラー) に飛ばさず入札画面をそのまま描画 / 全エリア有効とする
 * (取得失敗の通知はページ側 `failedDataLabels` バナーが担う。上記参照)。
 */
export function useListTradableAreas(
  deliveryDate: Date,
  options: { retainsPreviousData?: boolean; enabled?: boolean } = {},
): UseQueryResult<ListTradableAreasResponse, ConnectError> {
  return useQuery(
    listTradableAreas,
    options.enabled === false
      ? skipToken
      : create(ListTradableAreasRequestSchema, {
          deliveryDate: toProtoDate(deliveryDate),
        }),
    {
      throwOnError: false,
      placeholderData:
        options.retainsPreviousData && options.enabled !== false
          ? keepPreviousData
          : undefined,
    },
  )
}
